// The sidebar (parity/backoffice/routes.md §8, user / impersonating menu), as a typed tree. Each leaf names a route of
// the router and, where legacy gates it, the contract permission that unlocks it. Legacy filtering: a leaf is hidden
// when its permission is not held; a branch with no visible children is hidden. Cohort-0 entries only: areas The
// Owl Club does not use are not listed until their pages exist (Order Lookup, AI…).
import type { ReactNode } from 'react';
import { BadgePercent, ChartSpline, Component, FileSearch, Hamburger, IdCard, LayoutDashboard, Settings, Store, Users } from 'lucide-react';
import type { PermissionKey, SettingKey } from '@radish/contracts';
import type { NavNode } from '@radish/ui-web';
import { mayOpen } from '../app/page-permissions.ts';
import type { AppPath } from '../router-paths.ts';

interface NavSpec {
  readonly id: string;
  readonly label: string;
  readonly to?: AppPath;
  /** Link to the first of these the principal may open, else `to` (the Online Store's first permitted tab, Q-OS-01). */
  readonly toFirstOf?: readonly AppPath[];
  readonly icon?: ReactNode;
  readonly permission?: PermissionKey;
  /** A boolean location setting that must be on for the entry to show (legacy `config.table_mapping` for Floor Plans). */
  readonly setting?: SettingKey;
  /**
   * The billing feature the entry belongs to (billing slice 6): hidden while the location's plan is enforced and the
   * feature isn't on it (its page shows the upsell). Never a core feature: selling, payments, the KDS and reports.
   */
  readonly feature?: string;
  /** GATE-08: also shown to a holder of this key once the location is on a real plan (not Legacy). */
  readonly onPlanPermission?: PermissionKey;
  readonly badge?: string;
  /** Other paths that light this entry (a tab of the same page, a route legacy nests under it). */
  readonly match?: readonly AppPath[];
  /** Paths inside this group whose page no link of it names: the group opens, nothing in it is current. */
  readonly opensOn?: readonly AppPath[];
  readonly children?: readonly NavSpec[];
}

const icon = (node: ReactNode) => node;
// Legacy AppSidebarIcon: lucide at :size="20" :stroke-width="1.5" in a 29 px border-box with 12 px padding, so the glyph
// is 17 px with a 1.5 stroke in the 24 grid. Its 'id-card-lanyard' entry maps to IdCard, the landscape card (B2005).
const size = { size: 17, strokeWidth: 1.5, 'aria-hidden': true } as const;

export const SIDEBAR: readonly NavSpec[] = [
  { id: 'dashboard', label: 'Dashboard', to: '/l/$slug', icon: icon(<LayoutDashboard {...size} />) },
  {
    id: 'reports', label: 'Reports', icon: icon(<ChartSpline {...size} />), permission: 'reports.view',
    children: [
      {
        id: 'sales-reports', label: 'Sales Reports', permission: 'reports.sales.view',
        // Legacy opens Sales Reports on the Gift Cards welcome, with no link of it current (lane V's capture).
        opensOn: ['/l/$slug/reports/sales/gift-cards/welcome'],
        children: [
          { id: 'reports.sales.overview', label: 'Overview', to: '/l/$slug/reports/sales/overview' },
          { id: 'reports.sales.consolidated', label: 'EOD Reports', to: '/l/$slug/reports/sales/consolidated' },
          { id: 'reports.sales.items', label: 'Item Sales', to: '/l/$slug/reports/sales/items' },
          { id: 'reports.sales.category', label: 'Category Sales', to: '/l/$slug/reports/sales/category' },
          { id: 'reports.sales.department', label: 'Department Sales', to: '/l/$slug/reports/sales/department' },
          { id: 'reports.sales.employee', label: 'Employee Sales', to: '/l/$slug/reports/sales/employee' },
          { id: 'reports.sales.dining', label: 'Dining Option Sales', to: '/l/$slug/reports/sales/dining' },
          { id: 'reports.sales.time-segment', label: 'Time Segments', to: '/l/$slug/reports/sales/time-segment' },
          { id: 'reports.sales.discounts', label: 'Discounts', to: '/l/$slug/reports/sales/discounts' },
          { id: 'reports.sales.gift-cards', label: 'Gift Cards', to: '/l/$slug/reports/sales/gift-cards' },
        ],
      },
      {
        id: 'auditing-reports', label: 'Auditing Reports', permission: 'reports.auditing.view',
        // Legacy opens Auditing Reports on a drawer's detail, with Drawers/Banks not current (lane V's capture).
        opensOn: ['/l/$slug/reports/audit/drawer/$tillSessionId'],
        children: [
          { id: 'reports.audit.drawer', label: 'Drawers/Banks', to: '/l/$slug/reports/audit/drawer', match: ['/l/$slug/reports/audit/server-banks'] },
          { id: 'reports.audit.adjustments', label: 'Adjustments', to: '/l/$slug/reports/audit/adjustments/voids', match: ['/l/$slug/reports/audit/adjustments/deletion'] },
          { id: 'reports.audit.refunds', label: 'Refunds', to: '/l/$slug/reports/audit/refunds' },
        ],
      },
      { id: 'payroll-reports', label: 'Payroll Reports', permission: 'reports.payroll.view', children: [
        { id: 'reports.management.payroll', label: 'Payroll Summary', to: '/l/$slug/reports/management/payroll' },
        { id: 'reports.management.breaks', label: 'Breaks', to: '/l/$slug/reports/management/breaks' },
      ] },
    ],
  },
  { id: 'menu', label: 'Menu', to: '/l/$slug/menu/items/{-$itemId}', match: ['/l/$slug/menu/categories', '/l/$slug/menu/modifiers', '/l/$slug/menu/departments', '/l/$slug/menu/display-reorder', '/l/$slug/menu/visibility'], icon: icon(<Hamburger {...size} />), permission: 'items.manage' },
  { id: 'discounts', label: 'Discounts', to: '/l/$slug/discounts/preset', match: ['/l/$slug/discounts/auto', '/l/$slug/discounts/price-adjustments'], icon: icon(<BadgePercent {...size} />), permission: 'discounts.manage' },
  {
    id: 'employees', label: 'Employees', to: '/l/$slug/employees/list', match: ['/l/$slug/employees/timecards/{-$timecardId}'],
    icon: icon(<IdCard {...size} />), permission: 'labor.timecards.manage',
  },
  { id: 'customers', label: 'Customers', to: '/l/$slug/customers/list', icon: icon(<Users {...size} />), permission: 'customers.manage' },
  {
    id: 'online-store', label: 'Online Store', to: '/l/$slug/online-store/info', icon: icon(<Store {...size} />), permission: 'online.manage', feature: 'online_ordering',
    toFirstOf: [
      '/l/$slug/online-store/info', '/l/$slug/online-store/orders', '/l/$slug/online-store/hours-and-closures', '/l/$slug/online-store/prep-times',
      '/l/$slug/online-store/banners', '/l/$slug/online-store/notifications', '/l/$slug/online-store/voice-ordering', '/l/$slug/online-store/voice-conversations',
    ],
    match: ['/l/$slug/online-store/orders', '/l/$slug/online-store/hours-and-closures', '/l/$slug/online-store/prep-times', '/l/$slug/online-store/banners', '/l/$slug/online-store/notifications'],
  },
  { id: 'floor-plans', label: 'Floor Plans', to: '/l/$slug/floor-plans', icon: icon(<Component {...size} />), permission: 'floor.manage', setting: 'floor.table_mapping' },
  { id: 'order-lookup', label: 'Order Lookup', to: '/l/$slug/order/lookup', icon: icon(<FileSearch {...size} />), permission: 'order_lookup.view' },
  {
    id: 'settings', label: 'Settings', icon: icon(<Settings {...size} />),
    children: [
      {
        id: 'settings.location', label: 'Location', permission: 'settings.location.manage',
        children: [
          {
            id: 'settings.location.business-profile', label: 'Business Profile', to: '/l/$slug/settings/location/business-profile',
            match: ['/l/$slug/settings/location/business-profile/business-hours', '/l/$slug/settings/location/business-profile/branding'],
          },
          { id: 'settings.location.payment-configuration', label: 'Payment Configuration', to: '/l/$slug/settings/location/payment-configuration' },
          // GATE-08: staff and resellers always (legacy showed it when impersonating); a merchant with Location Settings
          // once the location is on a real plan (slice 6). On Legacy the server refuses the merchant's reads too.
          {
            id: 'settings.location.plans-and-billing', label: 'Plans & Billing', to: '/l/$slug/settings/location/plans-and-billing', permission: 'billing.subscription.manage',
            onPlanPermission: 'settings.location.manage',
          },
          { id: 'settings.business-day', label: 'Business Day', to: '/l/$slug/settings/location/business-day', permission: 'settings.business_day.manage' },
          { id: 'settings.location.users', label: 'Users', to: '/l/$slug/settings/location/users', permission: 'users.manage' },
        ],
      },
      // Legacy marks Hardware current on both of its tabs (route names hold 'hardware').
      { id: 'settings.hardware', label: 'Hardware', to: '/l/$slug/settings/hardware/devices', match: ['/l/$slug/settings/hardware/printers'], permission: 'hardware.manage' },
      {
        id: 'settings.staff-settings', label: 'Staff Settings', permission: 'settings.staff.manage',
        children: [
          {
            id: 'settings.staff-settings.timecards', label: 'Timecards', to: '/l/$slug/settings/staff-settings/timecards/employee-breaks', feature: 'timecards_payroll',
            // Legacy nests every Timecards tab under the Timecards route, so its link is current on each.
            match: ['/l/$slug/settings/staff-settings/timecards/report-settings', '/l/$slug/settings/staff-settings/timecards/overtime'],
          },
          { id: 'settings.staff-settings.employee-roles', label: 'Employee Roles', to: '/l/$slug/settings/staff-settings/employee-roles' },
          // Scheduling: platform admins only, as legacy's `loggedInAsAdmin` (G-M19, Q-SET-10).
          { id: 'settings.staff-settings.scheduling', label: 'Scheduling', to: '/l/$slug/settings/staff-settings/scheduling', permission: 'settings.platform.manage' },
          // Users and User Roles: Super Admin (or staff signed in as the user) only, legacy `isSuperAdmin` (A53).
          { id: 'settings.staff-settings.user-roles', label: 'User Roles', to: '/l/$slug/settings/staff-settings/user-roles', permission: 'users.manage' },
        ],
      },
      {
        id: 'settings.order-checkout', label: 'Order & Checkout', permission: 'settings.order_checkout.manage',
        children: [
          {
            id: 'settings.order-checkout.pos-functions', label: 'POS Functions', to: '/l/$slug/settings/order-checkout/pos-functions/register-settings',
            match: [
              '/l/$slug/settings/order-checkout/pos-functions/voids', '/l/$slug/settings/order-checkout/pos-functions/tender-types',
              '/l/$slug/settings/order-checkout/pos-functions/pacing', '/l/$slug/settings/order-checkout/pos-functions/dining-options',
              '/l/$slug/settings/order-checkout/pos-functions/id-verification',
              '/l/$slug/settings/order-checkout/pos-functions/drawers',
            ],
          },
          {
            id: 'settings.order-checkout.tips', label: 'Tips', to: '/l/$slug/settings/order-checkout/tips/customer-tips',
            match: ['/l/$slug/settings/order-checkout/tips/staff-tips'],
          },
          { id: 'settings.order-checkout.print-options', label: 'Print Options', to: '/l/$slug/settings/order-checkout/print-options' },
        ],
      },
      // Legacy L2 Reporting (manage_reporting_settings): Time Segments and Departments tabs (Report Settings is platform admin's).
      { id: 'settings.reporting', label: 'Reporting', to: '/l/$slug/settings/reporting/time-segments', match: ['/l/$slug/settings/reporting/departments'], permission: 'settings.reporting.manage' },
    ],
  },
];

/** The path's section, legacy's `parentPath`: the segment after `/l/$slug` ('' for the dashboard). */
const sectionOf = (path: AppPath): string => path.split('/')[3] ?? '';

/** What billing says about the location's menu (slice 6): which features are on, and whether it is on a real plan. */
interface NavGate {
  readonly entitled: (feature: string) => boolean;
  readonly onPlan: boolean;
}

/** Nothing known about billing: everything on, Legacy (the fail-open default). */
export const OPEN_GATE: NavGate = { entitled: () => true, onPlan: false };

/**
 * The visible tree for a principal at the current route: a leaf without its permission is dropped, a top-level entry
 * without its permission goes with everything under it (legacy filters level 1 first: no View Reports, no Reports at
 * all), an L2 without its own permission keeps children that are independently granted, and an empty branch disappears
 * (routes.md §8). An entry whose billing feature is off for the location is dropped (its page shows the upsell).
 */
export function visibleNav(
  specs: readonly NavSpec[], can: (p: PermissionKey) => boolean, currentPath: AppPath | null, inherited = true, on: (s: SettingKey) => boolean = () => true,
  gate: NavGate = OPEN_GATE, depth = 1,
): NavNode[] {
  return specs.flatMap((spec): NavNode[] => {
    if (spec.setting !== undefined && !on(spec.setting)) return [];
    if (spec.feature !== undefined && !gate.entitled(spec.feature)) return [];
    const onPlanGrant = spec.onPlanPermission !== undefined && gate.onPlan && can(spec.onPlanPermission);
    const granted = spec.permission === undefined ? inherited : can(spec.permission) || onPlanGrant;
    if (depth === 1 && spec.permission !== undefined && !granted) return [];
    if (spec.children !== undefined) {
      const children = visibleNav(spec.children, can, currentPath, granted, on, gate, depth + 1);
      if (children.length === 0) return [];
      // The page is inside the group though none of its links is current (a drawer's detail, the Gift Cards welcome).
      const opened = (currentPath !== null && (spec.opensOn ?? []).includes(currentPath)) || children.some((c) => c.opened === true);
      return [{ id: spec.id, label: spec.label, icon: spec.icon, active: false, children, ...(opened ? { opened } : {}), ...(spec.badge === undefined ? {} : { badge: spec.badge }) }];
    }
    if (!granted || spec.to === undefined) return [];
    const href = spec.toFirstOf?.find((p) => mayOpen(p, can)) ?? spec.to;
    // Legacy `isFirstLevelActive`: a top-level link is current anywhere in its section (the segment after the slug).
    const inSection = depth === 1 && currentPath !== null && sectionOf(spec.to) !== '' && sectionOf(spec.to) === sectionOf(currentPath);
    const active = currentPath !== null && (inSection || spec.to === currentPath || href === currentPath || (spec.match ?? []).includes(currentPath));
    return [{ id: spec.id, label: spec.label, href, icon: spec.icon, active, ...(spec.badge === undefined ? {} : { badge: spec.badge }) }];
  });
}
